<script lang="ts">
  // Types
  import type { GnDocsExampleFile } from './GnDocsExampleTabs.vue'

  // Context
  import { GnIcon } from '../GnIcon'

  export interface GnDocsExampleProps {
    /** Anchor id for deep linking */
    id?: string
    /** Single-file code body */
    code?: string
    /** Language for single-file mode */
    language?: string
    /** Filename for single-file mode */
    fileName?: string
    /** Files for multi-file mode (overrides single-file code) */
    files?: GnDocsExampleFile[]
    /** Display order for files (indices match files array) */
    fileOrders?: (number | undefined)[]
    /** Description title rendered in the header */
    title?: string
    /** Enable description collapse toggle */
    collapse?: boolean
    /** Peek mode: code is truncated below the preview with an expand button */
    peek?: boolean
    /** Visible rows when peeking */
    peekLines?: number
    /** Disable the splitter resize affordance */
    disableResize?: boolean
    /** Hide the splitter drag-width indicator */
    hideWidthIndicator?: boolean
    /**
     * Scope a v0 theme to the preview content. Forwarded to GnDocsExamplePreview;
     * applies `data-theme="<name>"` to the panel so the example renders in the
     * named v0 theme (e.g., "dark", "corporateIndigo"), independent of the page.
     */
    theme?: string
    /** Show "open in playground" action button on multi-file tab toolbar */
    showPlayground?: boolean
    /** Show "open in bin" action button on multi-file tab toolbar */
    showBin?: boolean
  }

  const RESET = 'M17.65,6.35C16.2,4.9 14.21,4 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20C15.73,20 18.84,17.45 19.73,14H17.65C16.83,16.33 14.61,18 12,18A6,6 0 0,1 6,12A6,6 0 0,1 12,6C13.66,6 15.14,6.69 16.22,7.78L13,11H20V4L17.65,6.35Z'
  const TOGGLE = 'M7.41,8.58L12,13.17L16.59,8.58L18,10L12,16L6,10L7.41,8.58Z'
</script>

<script setup lang="ts">
  // Context
  import GnDocsExampleActions from './GnDocsExampleActions.vue'
  import GnDocsExampleCode from './GnDocsExampleCode.vue'
  import GnDocsExampleDescription from './GnDocsExampleDescription.vue'
  import GnDocsExamplePreview from './GnDocsExamplePreview.vue'
  import GnDocsExampleTabs from './GnDocsExampleTabs.vue'

  // Utilities
  import { shallowRef, toRef, useId, useTemplateRef } from 'vue'

  import GnActionButton from '../GnActionButton/GnActionButton.vue'
  import GnPeek from '../GnPeek/GnPeek.vue'

  defineOptions({ name: 'GnDocsExample' })

  const {
    id,
    code,
    language = 'vue',
    fileName,
    files,
    fileOrders,
    title,
    collapse = false,
    peek = false,
    peekLines = 6,
    disableResize = false,
    hideWidthIndicator = false,
    theme,
    showPlayground = false,
    showBin = false,
  } = defineProps<GnDocsExampleProps>()

  const emit = defineEmits<{
    reset: []
    combine: [combined: boolean]
    playground: [files: GnDocsExampleFile[]]
    bin: [files: GnDocsExampleFile[]]
  }>()

  const uid = useId()
  const showCode = shallowRef(false)
  const peekExpanded = shallowRef(false)

  const previewRef = useTemplateRef<InstanceType<typeof GnDocsExamplePreview>>('preview')

  const hasMultipleFiles = toRef(() => (files?.length ?? 0) > 1)
  const hasCode = toRef(() => Boolean(code || files?.length))

  function toggleCode () {
    showCode.value = !showCode.value
  }

  function onReset () {
    previewRef.value?.reset()
    emit('reset')
  }

  function onCombine (combined: boolean) {
    emit('combine', combined)
  }

  function onPlayground (list: GnDocsExampleFile[]) {
    emit('playground', list)
  }

  function onBin (list: GnDocsExampleFile[]) {
    emit('bin', list)
  }
</script>

<template>
  <div
    :id
    class="genesis-docs-example"
    :data-expanded="showCode || undefined"
    :data-peek="peek || undefined"
  >
    <GnDocsExampleDescription
      v-if="title || $slots.description"
      :anchor-id="id"
      :collapse
      :title
    >
      <slot name="description" />
    </GnDocsExampleDescription>

    <GnDocsExamplePreview
      ref="preview"
      :disable-resize
      :show-width-indicator="!hideWidthIndicator"
      :theme
    >
      <slot />

      <template v-if="$slots.decoration" #decoration>
        <slot name="decoration" />
      </template>

      <template v-if="$slots['preview-actions']" #actions>
        <slot name="preview-actions" />
      </template>
    </GnDocsExamplePreview>

    <div v-if="hasCode && !peek" class="genesis-docs-example__toggle-bar">
      <button
        :aria-controls="`${uid}-code`"
        :aria-expanded="showCode"
        :aria-label="showCode ? 'Hide code' : 'Show code'"
        class="genesis-docs-example__toggle"
        data-tour="example-toggle"
        type="button"
        @click="toggleCode"
      >
        <slot :expanded="showCode" name="toggle-icon">
          <GnIcon
            class="genesis-docs-example__toggle-chevron"
            :d="TOGGLE"
            role="example-toggle"
            :size="14"
          />
        </slot>

        <span v-if="hasMultipleFiles" class="genesis-docs-example__meta">
          {{ files!.length }} file(s)
        </span>

        <span v-else-if="fileName || language" class="genesis-docs-example__meta">
          {{ fileName || language }}
        </span>
      </button>
    </div>

    <div
      v-if="(showCode || peek) && hasCode"
      :id="`${uid}-code`"
      class="genesis-docs-example__code"
    >
      <GnDocsExampleTabs
        v-if="hasMultipleFiles"
        :file-orders
        :files="files!"
        :show-bin
        :show-playground
        @bin="onBin"
        @combine="onCombine"
        @playground="onPlayground"
        @reset="onReset"
      >
        <template v-if="$slots.code" #default="{ code: paneCode, combined: paneCombined, file: paneFile, language: paneLanguage }">
          <slot
            :code="paneCode"
            :combined="paneCombined"
            :file="paneFile"
            :language="paneLanguage"
            name="code"
          />
        </template>

        <template v-if="$slots['reset-icon']" #reset-icon>
          <slot name="reset-icon" />
        </template>

        <template v-if="$slots['playground-icon']" #playground-icon>
          <slot name="playground-icon" />
        </template>

        <template v-if="$slots['bin-icon']" #bin-icon>
          <slot name="bin-icon" />
        </template>

        <template v-if="$slots['combine-icon']" #combine-icon>
          <slot name="combine-icon" />
        </template>

        <template v-if="$slots['split-icon']" #split-icon>
          <slot name="split-icon" />
        </template>
      </GnDocsExampleTabs>

      <template v-else-if="code">
        <div class="genesis-docs-example__code-bar">
          <span v-if="fileName" class="genesis-docs-example__code-filename">
            {{ fileName }}
          </span>

          <GnDocsExampleActions label="Example actions">
            <GnActionButton
              aria-label="Reset example"
              title="Reset example"
              @click="onReset"
            >
              <slot name="reset-icon">
                <GnIcon :d="RESET" role="example-reset" />
              </slot>
            </GnActionButton>
          </GnDocsExampleActions>
        </div>

        <GnDocsExampleCode
          v-model:expanded="peekExpanded"
          :code
          :file-name
          hide-filename
          hide-peek-toggle
          :language
          :peek
          :peek-lines
        >
          <template v-if="$slots.code" #default="{ code: paneCode, language: paneLanguage }">
            <slot
              :code="paneCode"
              :combined="false"
              :file="undefined"
              :language="paneLanguage"
              name="code"
            />
          </template>
        </GnDocsExampleCode>
      </template>
    </div>

    <GnPeek
      v-if="peek && !hasMultipleFiles && hasCode"
      v-model:expanded="peekExpanded"
      data-tour="example-expand"
    />
  </div>
</template>

<style scoped>
  .genesis-docs-example {
    position: relative;
    display: flex;
    flex-direction: column;
    border: 1px solid color-mix(in srgb, var(--v0-on-surface, currentcolor) 14%, transparent);
    border-radius: 0.5rem;
    background: var(--v0-surface, #fff);
    color: var(--v0-on-surface, #1a1c1e);
    /* The bottom hairline is drawn by the last child instead — see below. */
    border-bottom-color: transparent;
  }

  .genesis-docs-example__toggle-bar {
    position: sticky;
    top: var(--gn-docs-example-sticky-top, 0);
    z-index: 2;
    border-top: 1px solid color-mix(in srgb, var(--v0-on-surface, currentcolor) 14%, transparent);
    background: var(--v0-surface-tint, var(--v0-surface, #f5f5f8));
  }

  .genesis-docs-example[data-expanded] .genesis-docs-example__toggle-bar {
    border-bottom: 1px solid color-mix(in srgb, var(--v0-on-surface, currentcolor) 14%, transparent);
  }

  /* Peek mode drops the toggle bar, and with it the border-top that separates
     the preview from the code pane. Restore the divider on the pane itself so
     peek and non-peek examples read the same. */
  .genesis-docs-example[data-peek] .genesis-docs-example__code {
    border-top: 1px solid color-mix(in srgb, var(--v0-on-surface, currentcolor) 14%, transparent);
  }

  .genesis-docs-example > *:first-child:not(.genesis-peek) {
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
  }

  /* Draw the card's bottom hairline on the last child rather than on the card.
     Consumers put a backdrop-filter on these bars (the docs glass treatment),
     which composites the child into its own layer; that layer pixel-snaps
     outward and paints over the card's own border-bottom whenever the card's
     bottom edge lands below a half-pixel. A border on the child paints above
     its own filter, so it survives at any sub-pixel offset. The negative
     margin keeps it on the same row the card's border occupied, so the card's
     height and the corner radii are unchanged. */
  .genesis-docs-example > *:nth-last-child(1 of :not(.genesis-peek)) {
    border-bottom: 1px solid color-mix(in srgb, var(--v0-on-surface, currentcolor) 14%, transparent);
    margin-bottom: -1px;
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
  }

  .genesis-docs-example > *:nth-last-child(1 of :not(.genesis-peek)) .genesis-docs-example__toggle {
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
  }

  .genesis-docs-example__toggle {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    background: transparent;
    border: none;
    color: inherit;
    font: inherit;
    font-size: 0.875rem;
    cursor: pointer;
    transition: background-color 0.12s;
  }

  .genesis-docs-example__toggle:hover {
    background: var(--v0-surface, #fff);
  }

  .genesis-docs-example__toggle-chevron {
    transition: transform 0.15s;
  }

  .genesis-docs-example[data-expanded] .genesis-docs-example__toggle-chevron {
    transform: rotate(180deg);
  }

  .genesis-docs-example__meta {
    margin-inline-start: auto;
    color: var(--v0-on-surface-variant, rgb(0 0 0 / 0.6));
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 0.8125rem;
  }

  .genesis-docs-example__code {
    overflow: clip;
  }

  .genesis-docs-example__code-bar {
    position: sticky;
    top: calc(var(--gn-docs-example-sticky-top, 0px) + var(--gn-docs-example-toggle-h, 45px));
    z-index: 1;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem;
    background: var(--v0-surface, #fff);
    border-bottom: 1px solid color-mix(in srgb, var(--v0-on-surface, currentcolor) 14%, transparent);
    min-height: 3rem;
  }

  /* Peek has no Show-code bar, so this row sticks under the app bar. */
  .genesis-docs-example[data-peek] .genesis-docs-example__code-bar {
    top: var(--gn-docs-example-sticky-top, 0px);
  }

  .genesis-docs-example__code-filename {
    display: inline-flex;
    align-items: center;
    height: 30px;
    padding-inline: 0.5rem;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 0.75rem;
    font-weight: 500;
    color: var(--v0-on-primary, #fff);
    background: var(--v0-primary, #5f3aed);
    border-radius: 0.25rem;
    white-space: nowrap;
  }
</style>
